import React, { useState, useEffect } from "react";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useDashboardUI } from "../../context/GlobalContext";
import feedbackService from "../../services/feedbackService";
import { toast } from "react-toastify";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  RefreshCcw as ArrowPathIcon,
  TriangleAlert,
  MessageSquare,
  CheckCircle2,
  TrendingUp,
} from "lucide-react";

import { DateTime } from "luxon";

// Issue Categories bars go from a deep purple to a pale lavender by rank
const CATEGORY_BAR_SHADES = ["#6D28D9", "#8B5CF6", "#A78BFA", "#C4B5FD", "#DDD6FE"];

const FeedbackAnalytics = () => {
  const {
    feedbackStats,
    setFeedbackStats,
    statsLoading,
    setStatsLoading,
    statsError,
    setStatsError,
    isSuperAdmin,
    refreshTrigger,
    triggerRefresh,
    clearFeedbackSelection,
  } = useFeedback();

  const { selectedFacilityFilter } = useDashboardUI();

  const [dateRange, setDateRange] = useState({
    startDate: DateTime.now()
      .minus({ days: 30 })
      .startOf("day")
      .toFormat("yyyy-MM-dd"),
    endDate: DateTime.now().endOf("day").toFormat("yyyy-MM-dd"),
  });

  const fetchFeedbackStats = async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);

      const facilityId =
        isSuperAdmin && selectedFacilityFilter?.facility_number
          ? selectedFacilityFilter.facility_number
          : null;

      const stats = await feedbackService.getFeedbackStats(
        DateTime.fromISO(dateRange.startDate).startOf("day").toISO(),
        DateTime.fromISO(dateRange.endDate).endOf("day").toISO(),
        facilityId
      );

      setFeedbackStats(stats);
    } catch (error) {
      console.error("Error fetching feedback stats:", error);
      setStatsError("Failed to load feedback statistics");
      toast.error("Failed to load feedback statistics");
    } finally {
      setStatsLoading(false);
    }
  };

  const handleRefresh = () => fetchFeedbackStats();

  const handleDateRangeChange = (days) => {
    setDateRange({
      startDate: DateTime.now().minus({ days }).toFormat("yyyy-MM-dd"),
      endDate: DateTime.now().toFormat("yyyy-MM-dd"),
    });
  };

  useEffect(() => {
    fetchFeedbackStats();
  }, [dateRange, selectedFacilityFilter, refreshTrigger]);

  const prepareTrendData = () => {
    if (!feedbackStats?.daily_trend || !Array.isArray(feedbackStats.daily_trend) || feedbackStats.daily_trend.length === 0) return [];
    return feedbackStats.daily_trend.map((item) => {
      let dateStr = DateTime.now().toISODate();
      try {
        if (item.date) {
          const parsed = DateTime.fromISO(item.date);
          if (parsed.isValid) dateStr = parsed.toISODate();
        }
      } catch (error) {}
      return {
        date: dateStr,
        thumbs_up: parseInt(item.thumbs_up) || 0,
        thumbs_down: parseInt(item.thumbs_down) || 0,
      };
    });
  };

  const prepareCategoryData = () => {
    if (!feedbackStats?.category_breakdown || Object.keys(feedbackStats.category_breakdown).length === 0) return [];
    return Object.entries(feedbackStats.category_breakdown)
      .map(([category, count], index) => ({
        name: category.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        value: parseInt(count) || 0,
        color: getCategoryColor(category, index),
        originalKey: category,
      }))
      .sort((a, b) => b.value - a.value);
  };

  const getCategoryColor = (category, index = 0) => {
    const predefinedColors = {
      inaccurate_information: "#EF4444",
      unsafe_medical_advice: "#DC2626",
      not_helpful: "#F59E0B",
      code_error: "#F97316",
      response_too_long: "#84CC16",
      inappropriate_response: "#8B5CF6",
      phi_leak: "#EC4899",
      general_issue: "#6366F1",
      technical_error: "#F59E0B",
      user_interface: "#10B981",
    };
    const fallbackColors = ["#6B7280","#9CA3AF","#3B82F6","#1D4ED8","#10B981","#059669","#F59E0B","#D97706","#8B5CF6","#7C3AED","#EC4899","#DB2777","#06B6D4","#0891B2","#84CC16","#65A30D"];
    return predefinedColors[category] || fallbackColors[index % fallbackColors.length] || "#6B7280";
  };

  const trendData = prepareTrendData();
  const trendMax = trendData.reduce((m, d) => Math.max(m, d.thumbs_up, d.thumbs_down), 0);
  // Whole-number y ticks in 3 equal steps (0, s, 2s, 3s) so the axis always
  // covers the data and never shows repeated or fractional labels.
  const trendYStep = Math.max(1, Math.ceil(trendMax / 3));
  const trendYMax = trendYStep * 3;
  const trendYTicks = [0, trendYStep, trendYStep * 2, trendYMax];
  const categoryData = prepareCategoryData();

  const getActiveDays = () => {
    const start = DateTime.fromISO(dateRange.startDate);
    const end = DateTime.fromISO(dateRange.endDate);
    return Math.round(end.diff(start, "days").days);
  };

  const activeDays = getActiveDays();
  const isActive7D = activeDays <= 8;
  const isActive30D = activeDays > 8 && activeDays <= 31;
  const isActive90D = activeDays > 31;

  if (statsLoading && !feedbackStats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B3674]"></div>
        <span className="ml-3 text-gray-600">Loading analytics...</span>
      </div>
    );
  }

  if (statsError && !feedbackStats) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <TriangleAlert className="h-12 w-12 text-red-500 mb-4" />
        <p className="text-red-600 mb-4">{statsError}</p>
        <Button onClick={handleRefresh} variant="outline">
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const total = parseInt(feedbackStats?.total_feedback) || 0;
  const unaddressed = parseInt(feedbackStats?.unaddressed_count) || 0;
  const addressed = Math.max(0, total - unaddressed);
  const up = parseInt(feedbackStats?.thumbs_up_count) || 0;
  const down = parseInt(feedbackStats?.thumbs_down_count) || 0;
  const splitTotal = up + down;
  const upPct = splitTotal > 0 ? ((up / splitTotal) * 100).toFixed(1) : "0.0";
  const downPct = splitTotal > 0 ? ((down / splitTotal) * 100).toFixed(1) : "0.0";
  const upBarPct = splitTotal > 0 ? (up / splitTotal) * 100 : 0;
  const addressedPct = total > 0 ? ((addressed / total) * 100).toFixed(0) : "0";
  const rangeLabel = isActive7D ? "Last 7 days" : isActive30D ? "Last 30 days" : "Last 90 days";

  return (
    <div className="space-y-6" style={{ fontFamily: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif' }}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-[#14122B]">Performance Overview</h3>
          {/* Phones: refresh icon sits beside the heading (the top Refresh button is hidden on this tab) */}
          <button
            type="button"
            onClick={() => {
              clearFeedbackSelection();
              triggerRefresh();
            }}
            disabled={statsLoading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white text-slate-700 shadow-sm hover:border-gray-300 transition-colors disabled:opacity-60 sm:hidden"
          >
            <ArrowPathIcon className={`h-4 w-4 ${statsLoading ? "animate-spin" : ""}`} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {[
            { label: "7D", days: 7, active: isActive7D },
            { label: "30D", days: 30, active: isActive30D },
            { label: "90D", days: 90, active: isActive90D },
          ].map(({ label, days, active }) => (
            <Button
              key={label}
              variant="outline"
              size="sm"
              onClick={() => handleDateRangeChange(days)}
              className={active ? "bg-[#14122B] text-white border-[#14122B] hover:bg-[#14122B] hover:text-white" : ""}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

        {/* Total Feedback */}
        <Card className="rounded-2xl border-0 bg-white shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
          <div className="p-6 flex flex-col gap-4">

            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-[#8A91A5] uppercase tracking-wide">Total Feedback</p>
              <div className="h-9 w-9 rounded-xl bg-[#F3EAFE] flex items-center justify-center">
                <MessageSquare className="h-4 w-4 text-[#8B5CF6]" />
              </div>
            </div>

            <p className="text-4xl font-extrabold tracking-tight text-[#14122B] leading-none">{total.toLocaleString()}</p>
            <div className="border-t border-[#EEF0F5]" />

            {/* Breakdown */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-[#16B364]" />
                <span className="text-sm font-medium text-[#4B5266]">Positive</span>
              </div>
              <span className="text-sm font-bold text-[#14122B]">{up.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between -mt-2">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-[#E5484D]" />
                <span className="text-sm font-medium text-[#4B5266]">Negative</span>
              </div>
              <span className="text-sm font-bold text-[#14122B]">{down.toLocaleString()}</span>
            </div>

            <p className="text-xs text-[#9AA1B2] -mt-1">{rangeLabel}</p>
          </div>
        </Card>

        {/* Open Issues */}
        <Card className="rounded-2xl border-0 bg-white shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
          <div className="p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-[#8A91A5] uppercase tracking-wide">Open Issues</p>
              <div className="h-9 w-9 rounded-xl bg-[#F1F2F7] flex items-center justify-center">
                {unaddressed > 0
                  ? <TriangleAlert className="h-4 w-4 text-[#4B5266]" />
                  : <CheckCircle2 className="h-4 w-4 text-[#4B5266]" />
                }
              </div>
            </div>

            <p className={`text-4xl font-extrabold tracking-tight leading-none text-[#14122B]`}>
              {unaddressed.toLocaleString()}
            </p>

            <div className="border-t border-[#EEF0F5]" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-[#16B364]" />
                <span className="text-sm font-medium text-[#4B5266]">Addressed</span>
              </div>
              <span className="text-sm font-bold text-[#14122B]">{addressed.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between -mt-2">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-[#C9CDD8]" />
                <span className="text-sm font-medium text-[#4B5266]">Resolution rate</span>
              </div>
              <span className="text-sm font-bold text-[#14122B]">{addressedPct}%</span>
            </div>

            {unaddressed > 0 ? (
              <span className="inline-flex items-center w-fit rounded-full bg-[#FDECEC] text-[#D93636] px-3 py-1 text-xs font-semibold -mt-1">Requires attention</span>
            ) : (
              <span className="inline-flex items-center w-fit rounded-full bg-[#E7F7EE] text-[#12945A] px-3 py-1 text-xs font-semibold -mt-1">All caught up</span>
            )}
          </div>
        </Card>

        {/* Sentiment Split */}
        <Card className="rounded-2xl border-0 bg-white shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
          <div className="p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-[#8A91A5] uppercase tracking-wide">Sentiment</p>
              <div className="h-9 w-9 rounded-xl bg-[#F1F2F7] flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-[#4B5266]" />
              </div>
            </div>

            <div className="flex items-end gap-2 leading-none">
              <p className="text-4xl font-extrabold tracking-tight text-[#14122B]">{upPct}%</p>
              <p className="text-sm text-[#8A91A5] mb-1.5">positive</p>
            </div>

            {splitTotal > 0 ? (
              <div>
                <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-[#FBC8C8]">
                  <div
                    className="h-full bg-[#16B364] transition-all duration-500"
                    style={{ width: `${upBarPct}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-[#12945A] font-semibold">{up} positive</span>
                  <span className="text-xs text-[#D93636] font-semibold">{down} negative</span>
                </div>
              </div>
            ) : (
              <div className="h-2.5 w-full rounded-full bg-[#F1F2F7]" />
            )}

            <p className="text-xs text-[#9AA1B2] -mt-1">{downPct}% negative · {rangeLabel}</p>
          </div>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-2xl border-0 bg-white shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)] p-6">
          <div className="flex flex-wrap items-center justify-between gap-x-3 min-h-6 mb-4">
            <h4 className="text-base font-bold text-[#14122B] leading-6">Satisfaction Trend</h4>
            <div className="flex items-center gap-3.5 h-6">
              <div className="flex items-center gap-1.5 h-6">
                <div className="h-0.5 w-3.5 rounded-full bg-[#16B364]" />
                <span className="text-xs font-semibold text-[#12945A] leading-6">Positive</span>
              </div>
              <div className="flex items-center gap-1.5 h-6">
                <div className="h-0.5 w-3.5 rounded-full bg-[#E5484D]" />
                <span className="text-xs font-semibold text-[#D93636] leading-6">Negative</span>
              </div>
            </div>
          </div>
          {trendData.length > 0 ? (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trendData} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#E8EAF0" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={{ stroke: "#E5E7EB" }}
                    tick={{ fill: "#9AA1B2", fontSize: 10 }}
                    tickMargin={8}
                    interval="preserveStartEnd"
                    minTickGap={48}
                    tickFormatter={(v) => DateTime.fromISO(v).toFormat("LLL d")}
                    height={38}
                    label={{ value: "Date", position: "insideBottom", offset: 0, fill: "#9AA1B2", fontSize: 11, fontWeight: 600 }}
                  />
                  <YAxis
                    width={44}
                    label={{ value: "Feedback count", angle: -90, position: "insideLeft", offset: 12, style: { textAnchor: "middle" }, fill: "#9AA1B2", fontSize: 11, fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9AA1B2", fontSize: 10 }}
                    domain={[0, trendYMax]}
                    ticks={trendYTicks}
                    allowDecimals={false}
                  />
                  <Tooltip
                    labelFormatter={(v) => DateTime.fromISO(v).toFormat("LLL d, yyyy")}
                    formatter={(value, name) => [value, name === "thumbs_up" ? "Positive" : "Negative"]}
                    contentStyle={{ borderRadius: 8, border: "1px solid #EEF0F5", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", fontSize: 12 }}
                  />
                  <Line type="linear" dataKey="thumbs_up" stroke="#16B364" strokeWidth={2.5} dot={trendData.length === 1} activeDot={{ r: 4 }} />
                  <Line type="linear" dataKey="thumbs_down" stroke="#E5484D" strokeWidth={2.5} dot={trendData.length === 1} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-400 text-sm">
              No trend data available
            </div>
          )}
        </Card>

        <Card className="rounded-2xl border-0 bg-white shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)] p-6">
          <div className="mb-4">
            <h4 className="text-base font-bold text-[#14122B]">Issue Categories</h4>
            <p className="text-xs text-[#9AA1B2] mt-0.5">Negative feedback by category · {rangeLabel.toLowerCase()}</p>
          </div>
          {categoryData.length > 0 ? (
            <div className="flex flex-col gap-3.5 max-h-[300px] overflow-y-auto pr-1">
              {categoryData.map((cat, i) => {
                const maxVal = categoryData[0].value || 1;
                return (
                  <div key={cat.originalKey}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-[#14122B] truncate">{cat.name}</span>
                      <span className="text-sm font-bold text-[#14122B] tabular-nums ml-3">{cat.value.toLocaleString()}</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-[#F1F0F7] overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${(cat.value / maxVal) * 100}%`,
                          background: CATEGORY_BAR_SHADES[Math.min(i, CATEGORY_BAR_SHADES.length - 1)],
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-400 text-sm">
              No category data available
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default FeedbackAnalytics;